import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { User, Session } from "@supabase/supabase-js";
import {
  supabase,
  AuthUser,
  uploadAvatar,
  removeAvatar,
  resizeImage,
  initializeDailyChallenges,
  recoverSession,
} from "../lib/supabase";
import {
  signInWithPassword as authSignInWithPassword,
  signUp as authSignUp,
  signOut as authSignOut,
  resetPasswordForEmail as authResetPasswordForEmail,
  updateUserPassword as authUpdateUserPassword,
} from "../lib/authUtils";
import toast from "react-hot-toast";

// Global flag to prevent multiple auth initializations (similar to security)
declare global {
  interface Window {
    __adapteach_auth_initialized?: boolean;
  }
}

interface AuthContextType {
  user: AuthUser | null;
  session: Session | null;
  loading: boolean;
  justRegistered: boolean;
  justLoggedIn: boolean;
  setJustRegistered: (value: boolean) => void;
  setJustLoggedIn: (value: boolean) => void;
  signUp: (
    email: string,
    password: string,
    userData: {
      name: string;
      asal_sekolah: string;
      role: string;
    }
  ) => Promise<{ success: boolean; error?: string }>;
  signIn: (
    email: string,
    password: string
  ) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  resetPassword: (
    email: string
  ) => Promise<{ success: boolean; error?: string }>;
  updateUserProfile: (
    userId: string,
    profileData: {
      name: string;
      asal_sekolah: string;
      role: string;
      avatar_url?: string | null;
    }
  ) => Promise<{ success: boolean; error?: string }>;
  updateUserPassword: (
    currentPassword: string,
    newPassword: string
  ) => Promise<{ success: boolean; error?: string }>;
  uploadUserAvatar: (
    userId: string,
    file: File
  ) => Promise<{ success: boolean; error?: string }>;
  removeUserAvatar: (
    userId: string
  ) => Promise<{ success: boolean; error?: string }>;
  refreshUserProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [justRegistered, setJustRegistered] = useState(false);
  const [justLoggedIn, setJustLoggedIn] = useState(false);
  const isMounted = useRef(true);

  const isInitialized = useRef(false);
  const authSubscription = useRef<{ unsubscribe: () => void } | null>(null);
  const initializationLock = useRef(false);

  // Fetch user profile from database
  const fetchUserProfile = async (authUser: User) => {
    try {
      // ✅ FIX: Langsung gunakan authUser.id, jangan panggil supabase.auth.getUser() lagi.
      const { data: profile, error } = await supabase
        .from("user_profiles")
        .select("*")
        .eq("id", authUser.id)
        .maybeSingle();

      if (error) {
        // Jika ada error (misal profil belum dibuat), tetap set user
        // dengan data minimal agar aplikasi tidak crash.
        console.error("❌ Error fetching user profile:", error.message, error);
        if (isMounted.current) {
          setUser({
            id: authUser.id,
            email: authUser.email || "",
            profile: undefined,
          });
        }
        return;
      }

      // Jika tidak ada error, set user dengan profil lengkap
      if (isMounted.current) {
        setUser({
          id: authUser.id,
          email: authUser.email || "",
          profile: profile || undefined,
        });
      }
    } catch (error) {
      // Tangani exception jika terjadi
      console.error("💥 Exception in fetchUserProfile:", error);
      if (isMounted.current) {
        setUser({
          id: authUser.id,
          email: authUser.email || "",
          profile: undefined,
        });
      }
    }
  };

  useEffect(() => {
    isMounted.current = true;

    // Cleanup function to set mounted to false when component unmounts
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    // Check global flag first
    if (typeof window !== "undefined" && window.__adapteach_auth_initialized) {
      console.log(
        "🔄 Auth already initialized globally, setting up listener and checking session"
      );
      setLoading(false);

      // Still need to set up auth listener and check current session
      const setupAuthListener = async () => {
        try {
          console.log(
            "🔄 Setting up auth listener and checking current session..."
          );

          // Check current session
          const {
            data: { session },
            error: sessionError,
          } = await supabase.auth.getSession();

          if (sessionError) {
            console.error("❌ Error getting current session:", sessionError);
          } else if (session) {
            console.log("📡 Found existing session, updating state...");
            setSession(session);
            if (session.user) {
              await fetchUserProfile(session.user);
            }
          } else {
            console.log("📡 No existing session found");
          }

          // Set up auth state change listener
          if (!authSubscription.current) {
            console.log(
              "🔗 Setting up auth state change listener (global flag branch)..."
            );

            const {
              data: { subscription },
            } = // Di dalam useEffect initializeAuth, ganti bagian onAuthStateChange:
              supabase.auth.onAuthStateChange(async (event, session) => {
                console.log(
                  "🔄 Auth state changed:",
                  event,
                  session ? "Session exists" : "No session"
                );

                // Skip initial SIGNED_IN event if we just processed the session
                if (event === "SIGNED_IN" && !isInitialized.current) {
                  console.log(
                    "🔄 Skipping initial SIGNED_IN event during initialization"
                  );
                  return;
                }

                try {
                  // Only update state if component is still mounted
                  if (!isMounted.current) return;

                  if (event === "PASSWORD_RECOVERY") {
                    console.log("🔑 Password recovery event detected");
                    setSession(session);
                    if (session?.user) {
                      await fetchUserProfile(session.user);
                    }
                    return;
                  }

                  if (event === "USER_UPDATED") {
                    console.log("👤 User updated event detected");
                    setSession(session);
                    return;
                  }

                  // Event lainnya
                  if (event === "SIGNED_IN") {
                    setSession(session);
                    if (session?.user) {
                      await fetchUserProfile(session.user);
                    }
                    return;
                  }

                  if (event === "SIGNED_OUT") {
                    setSession(null);
                    setUser(null);
                    return;
                  }

                  // fallback default
                  setSession(session);
                  if (session?.user) {
                    await fetchUserProfile(session.user);
                  } else {
                    setUser(null);
                  }
                } catch (error) {
                  console.error("💥 Error in onAuthStateChange:", error);
                  // Set user to null on error to prevent app crash
                  if (isMounted.current) {
                    setUser(null);
                  }
                }
              });

            authSubscription.current = subscription;
            console.log("✅ Auth state change listener set up successfully");

            // Ensure global flag is set after listener is ready
            if (typeof window !== "undefined") {
              window.__adapteach_auth_initialized = true;
              console.log(
                "🔒 Auth marked as globally initialized (global flag branch)"
              );
            }
          }
        } catch (error) {
          console.error("💥 Error setting up auth listener:", error);
          // Reset global flag on error so initialization can be retried
          if (typeof window !== "undefined") {
            window.__adapteach_auth_initialized = false;
          }
        }
      };

      setupAuthListener()
        .then(() => {
          console.log("✅ Auth listener setup completed");
        })
        .catch(error => {
          console.error("💥 Auth listener setup failed:", error);
          // Reset global flag on error
          if (typeof window !== "undefined") {
            window.__adapteach_auth_initialized = false;
          }
        });

      return;
    }

    // Prevent multiple initializations with lock
    if (isInitialized.current || initializationLock.current) {
      console.log(
        "🔄 Auth initialization already done or in progress, skipping..."
      );
      return;
    }
    initializationLock.current = true;
    isInitialized.current = true;

    const initializeAuth = async () => {
      try {
        console.log("🔄 Initializing authentication...");

        // Use enhanced session recovery
        const recoveryResult = await recoverSession();

        if (!recoveryResult.success) {
          console.error("❌ Session recovery failed:", recoveryResult.error);
        }

        // Get current session (after potential recovery)
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          console.error("❌ Error getting session after recovery:", error);
          // Don't return here, continue to set loading to false
        } else {
          console.log(
            "📡 Session retrieved:",
            session ? "Session found" : "No session"
          );
        }

        // Only update state if component is still mounted
        if (!isMounted.current) return;

        setSession(session);

        if (session?.user) {
          console.log("👤 User found in session, fetching profile...");
          await fetchUserProfile(session.user);
        } else {
          console.log("👤 No user in session");
          setUser(null);
        }

        // Set up auth state change listener AFTER initial auth check
        // This prevents race conditions during initialization
        if (isMounted.current && !authSubscription.current) {
          console.log("🔗 Setting up auth state change listener...");
          const {
            data: { subscription },
          } = supabase.auth.onAuthStateChange(async (event, session) => {
            // Skip initial SIGNED_IN event if we just processed the session
            if (event === "SIGNED_IN" && !isInitialized.current) {
              console.log(
                "🔄 Skipping initial SIGNED_IN event during initialization"
              );
              return;
            }

            console.log(
              "🔄 Auth state changed:",
              event,
              session ? "Session exists" : "No session"
            );

            try {
              // Only update state if component is still mounted
              if (!isMounted.current) return;

              if (event === "PASSWORD_RECOVERY") {
                console.log("🔑 Password recovery event detected");
                setSession(session);
                if (session?.user) {
                  await fetchUserProfile(session.user);
                }
                return;
              }

              if (event === "USER_UPDATED") {
                console.log(
                  "👤 User updated event detected (in initializeAuth), updating session only."
                );
                setSession(session);
                return;
              }

              setSession(session);

              if (session?.user) {
                console.log(
                  "👤 User found in auth change, fetching profile..."
                );
                await fetchUserProfile(session.user);
              } else {
                console.log("👤 No user in auth change");
                setUser(null);
              }
            } catch (error) {
              console.error("💥 Error in onAuthStateChange:", error);
              // Set user to null on error to prevent app crash
              if (isMounted.current) {
                setUser(null);
              }
            }
          });
          authSubscription.current = subscription;

          // Mark as globally initialized only after auth listener is set up
          if (typeof window !== "undefined") {
            window.__adapteach_auth_initialized = true;
            console.log("🔒 Auth marked as globally initialized");
          }
        }
      } catch (error) {
        console.error("💥 Error in initializeAuth:", error);
        // Set user to null on error to prevent app crash
        if (isMounted.current) {
          setUser(null);
          setSession(null);
        }
      } finally {
        // Always set loading to false after initialization attempt
        // Use a small delay to ensure this runs even if component is remounting
        setTimeout(() => {
          if (isMounted.current) {
            console.log(
              "✅ Authentication initialization complete, setting loading to false"
            );
            setLoading(false);
          }
        }, 50);
        // Release initialization lock
        initializationLock.current = false;
      }
    };

    // Initialize authentication
    initializeAuth();

    // Cleanup subscription on unmount
    return () => {
      console.log("🧹 Cleaning up auth subscription");
      if (authSubscription.current) {
        authSubscription.current.unsubscribe();
        authSubscription.current = null;
      }
    };
  }, []);

  const refreshUserProfile = async () => {
    const { getCurrentUser } = await import("../lib/authUtils");
    const { user: currentUser, error } = await getCurrentUser();
    if (error || !currentUser) {
      return;
    }
    await fetchUserProfile(currentUser);
  };

  const signUp = async (
    email: string,
    password: string,
    userData: { name: string; asal_sekolah: string; role: string }
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      setJustRegistered(true);

      const { data, error } = await authSignUp(email, password, {
        data: {
          name: userData.name,
          asal_sekolah: userData.asal_sekolah,
          role: userData.role,
        },
      });

      if (error) {
        setJustRegistered(false);
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Terjadi kesalahan saat mendaftar";
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      }

      if (data?.user) {
        // Create user profile
        const { error: profileError } = await supabase
          .from("user_profiles")
          .insert({
            id: data.user.id,
            email: data.user.email,
            name: userData.name,
            asal_sekolah: userData.asal_sekolah,
            role: userData.role,
            avatar_url: "/avatars/ava1.png",
            points: 0,
          });

        if (!profileError) {
          await initializeDailyChallenges(data.user.id);
          await fetchUserProfile(data.user);
        }

        return { success: true };
      }

      setJustRegistered(false);
      return { success: false, error: "Terjadi kesalahan saat membuat akun" };
    } catch (error) {
      setJustRegistered(false);
      const errorMessage =
        error instanceof Error ? error.message : "Terjadi kesalahan";
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const signIn = async (
    email: string,
    password: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      setJustRegistered(false);
      setJustLoggedIn(true);

      const { data, error } = await authSignInWithPassword(email, password);

      if (error) {
        setJustLoggedIn(false);
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Terjadi kesalahan saat masuk";
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      }

      if (data?.user) {
        await initializeDailyChallenges(data.user.id);
        toast.success("Berhasil masuk!");
        return { success: true };
      }

      return { success: false, error: "Terjadi kesalahan saat masuk" };
    } catch (error) {
      setJustLoggedIn(false);
      const errorMessage =
        error instanceof Error ? error.message : "Terjadi kesalahan";
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      const { error } = await authSignOut();

      if (error) {
        toast.error("Gagal keluar");
        return;
      }

      setJustRegistered(false);
      setUser(null);
      setSession(null);
      toast.success("Berhasil keluar");
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Terjadi kesalahan";
      toast.error(errorMessage);
    }
  };

  const resetPassword = async (
    email: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await authResetPasswordForEmail(email);

      if (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Terjadi kesalahan";
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      }

      toast.success(
        "Jika email terdaftar, link reset password telah dikirim ke email Anda. Silakan periksa inbox dan folder spam."
      );
      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Terjadi kesalahan";
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const updateUserProfile = async (
    userId: string,
    profileData: {
      name: string;
      asal_sekolah: string;
      role: string;
      avatar_url?: string | null;
    }
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase
        .from("user_profiles")
        .update({
          name: profileData.name,
          asal_sekolah: profileData.asal_sekolah,
          role: profileData.role,
          avatar_url: profileData.avatar_url,
          updated_at: new Date().toISOString(),
        })
        .eq("id", userId);

      if (error) {
        toast.error("Gagal memperbarui profil: " + error.message);
        return { success: false, error: error.message };
      }

      if (user) {
        setUser({
          ...user,
          profile: {
            ...user.profile!,
            name: profileData.name,
            asal_sekolah: profileData.asal_sekolah,
            role: profileData.role,
            avatar_url: profileData.avatar_url || undefined,
            updated_at: new Date().toISOString(),
          },
        });
      }

      toast.success("Profil berhasil diperbarui!");
      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Terjadi kesalahan";
      toast.error("Gagal memperbarui profil: " + errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const updateUserPassword = async (
    _currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      if (!session) {
        toast.error("Sesi tidak aktif. Silakan login kembali.");
        return { success: false, error: "Sesi tidak aktif" };
      }

      const { error } = await authUpdateUserPassword(newPassword);

      if (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Gagal mengubah password";
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      }

      toast.success("Password berhasil diubah!");
      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Terjadi kesalahan tidak terduga";
      toast.error("Gagal mengubah password: " + errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const uploadUserAvatar = async (
    userId: string,
    file: File
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const currentAvatarUrl = user?.profile?.avatar_url;

      if (currentAvatarUrl) {
        await removeAvatar(userId, currentAvatarUrl);
      }

      const resizedFile = await resizeImage(file);
      const uploadResult = await uploadAvatar(userId, resizedFile);

      if (!uploadResult.success) {
        return { success: false, error: uploadResult.error };
      }

      const { error } = await supabase
        .from("user_profiles")
        .update({
          avatar_url: uploadResult.url,
          updated_at: new Date().toISOString(),
        })
        .eq("id", userId);

      if (error) {
        toast.error("Gagal memperbarui profil dengan avatar baru");
        return {
          success: false,
          error: "Gagal memperbarui profil dengan avatar baru",
        };
      }

      if (user && user.profile) {
        setUser({
          ...user,
          profile: {
            ...user.profile,
            avatar_url: uploadResult.url,
            updated_at: new Date().toISOString(),
          },
        });
      }

      toast.success("Avatar berhasil diperbarui!");
      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Terjadi kesalahan";
      toast.error("Gagal mengunggah avatar: " + errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const removeUserAvatar = async (
    userId: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const currentAvatarUrl = user?.profile?.avatar_url;

      await removeAvatar(userId, currentAvatarUrl);

      const { error } = await supabase
        .from("user_profiles")
        .update({
          avatar_url: "/avatars/ava1.png",
          updated_at: new Date().toISOString(),
        })
        .eq("id", userId);

      if (error) {
        toast.error("Gagal memperbarui profil");
        return { success: false, error: "Gagal memperbarui profil" };
      }

      if (user && user.profile) {
        setUser({
          ...user,
          profile: {
            ...user.profile,
            avatar_url: "/avatars/ava1.png",
            updated_at: new Date().toISOString(),
          },
        });
      }

      toast.success("Avatar berhasil direset ke default!");
      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Terjadi kesalahan";
      toast.error("Gagal menghapus avatar: " + errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    justRegistered,
    justLoggedIn,
    setJustRegistered,
    setJustLoggedIn,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateUserProfile,
    updateUserPassword,
    uploadUserAvatar,
    removeUserAvatar,
    refreshUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AuthContext };
